import { Controller } from "@hotwired/stimulus"

// Settings → Payments: the dual-pricing knob, previewed live on a real unit.
//
// The math here MIRRORS Billing::CardAdjustment exactly — integer cents,
// half-up — so the preview shows the same posted price the server will actually
// charge. Math.round matches Ruby's Float#round for the positive halves we deal
// in. If you change the rounding in one place, change it in the other.
//
// There is no "+3%" line anywhere in here, and there must never be: the posted
// price IS the price, and cash/check/bank transfer gets a DISCOUNT off it. A fee
// added on top at checkout is a surcharge, which is what this whole feature
// exists to stop doing.
export default class extends Controller {
  static targets = [
    "toggle", "percent", "percentRow", "absorbNote", "absorbed",
    "posted", "cash", "cashRow", "noChange", "alreadyPays", "increase", "newPays"
  ]

  static values = {
    baseCents: Number,      // the previewed unit's rent
    baselineBps: Number,    // what card payers pay above rent TODAY (0 when off)
    feeBps: Number,         // Payment::STRIPE_FEE_BPS
    feeFixedCents: Number   // Payment::STRIPE_FEE_FIXED_CENTS
  }

  connect() { this.recalc() }

  recalc() {
    const base = this.baseCentsValue
    const bps = this.#effectiveBps()

    const posted = base + this.#adjustmentCents(base, bps)

    this.postedTarget.textContent = this.#money(posted)
    this.cashTarget.textContent = this.#money(base)
    // No adjustment ⇒ no discount ⇒ no discount row. Showing "Posted price $120.00
    // / Cash, check or bank transfer $120.00" is a second price where the model
    // guarantees one.
    this.cashRowTarget.classList.toggle("hidden", bps <= 0)

    // The percent field is meaningless while the toggle is off; the "you absorb
    // the fee" note takes its place.
    this.percentRowTarget.classList.toggle("hidden", !this.toggleTarget.checked)
    this.absorbNoteTarget.classList.toggle("hidden", bps > 0)
    this.absorbedTarget.textContent = this.#money(this.#stripeFeeCents(base))

    // Nobody's payment changes when the rate is exactly what they already pay —
    // that is the reassurance the migration needs. Above it, they pay more, and
    // that needs saying out loud (a warning, never a block).
    const baseline = this.baselineBpsValue
    const unchanged = bps > 0 && bps === baseline
    const raised = bps > baseline

    this.noChangeTarget.classList.toggle("hidden", !unchanged)
    this.increaseTarget.classList.toggle("hidden", !raised)

    // "What they pay today" appears in both banners — hence the plural target.
    const paysToday = base + this.#adjustmentCents(base, baseline)
    this.alreadyPaysTargets.forEach((el) => { el.textContent = this.#money(paysToday) })
    this.newPaysTarget.textContent = this.#money(posted)
  }

  // Billing::CardAdjustment.enabled? — the toggle AND a positive rate.
  #effectiveBps() {
    if (!this.toggleTarget.checked) return 0
    const pct = parseFloat(this.percentTarget.value)
    if (Number.isNaN(pct) || pct <= 0) return 0
    return Math.min(600, Math.round(pct * 100))
  }

  // Billing::CardAdjustment.adjustment_cents — half-up, integer cents.
  #adjustmentCents(baseCents, bps) {
    if (bps <= 0 || baseCents <= 0) return 0
    return Math.round(baseCents * bps / 10000)
  }

  // Payment#estimated_stripe_fee_cents — what the operator eats with this off.
  #stripeFeeCents(baseCents) {
    return Math.round(baseCents * this.feeBpsValue / 10000) + this.feeFixedCentsValue
  }

  #money(cents) {
    return (cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD" })
  }
}
